import { useShallow } from 'zustand/react/shallow';
import { useLocalStorage, useLocation } from 'react-use';
import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { type AutogenConfig, type PendingUserAccountItem, PendingUserAccountItemType } from '@pkgs/components/types/types';
import { useMapVisibilityStore } from '@pkgs/components/providers/MapVisibilityStoreProvider';
import { Breakpoints } from '@pkgs/components/providers/BreakpointProvider';
import { useUserAccount } from '@pkgs/components/hooks/useUserAccount';
import { usePendingUserAccountItemAfterAuth } from '@pkgs/components/hooks/usePendingUserAccountItemAfterAuth';
import { useBreakpoint } from '@pkgs/components/hooks/useBreakpoint';
import { useAppContext } from '@pkgs/components/hooks/useAppContext';
import { useDisclaimerTextVisibilityStore } from '@pkgs/components/hooks/stores/useDisclaimerTextVisibilityStore';
import { useSearchStore } from '@pkgs/components/hooks/stores/search/useSearchStore';
import { usePromptStore } from '@pkgs/components/hooks/stores/search/usePromptStore';
import { SearchMode } from '@pkgs/components/hooks/stores/search/slices/searchModeSlice';
import { useListings } from '@pkgs/components/hooks/queries/useListings';
import { useAiSearch } from '@pkgs/components/hooks/queries/useAiSearch';
import { PENDING_USER_ACCOUNT_ITEM_KEY } from '@pkgs/components/consts/consts';
import { Trigger } from '@pkgs/components/components/UserAccountModal/UserAccountModal';
import { SEARCH_ALERT_CTA_ELEMENT_ID } from '@pkgs/components/components/SearchAlertCtaCard/SearchAlertCtaCard';
import { InfoRow } from '@pkgs/components/components/InfoRow/InfoRow';
import { useAuth } from '@ev-tech/user-account-modules';
import { useModalState } from '@ev-tech/ui-core-components/Modal';
import { definePageSkill, useRegisterPageSkill } from '@ev-tech/search-modules-chatbot';

import { SearchResultList } from './SearchResultList';
import { MapViewButton } from './MapViewButton';
import { ConditionalMapContainer } from './ConditionalMapContainer';
import { WiderAreaResults } from '../WiderAreaResults/WiderAreaResults';
import { UnsubscribeDialog } from '../UnsubscribeDialog/UnsubscribeDialog';
import { StickyFooter } from '../StickyFooter/StickyFooter';
import { SearchAlertButton } from '../SearchAlert/SearchAlertButton';
import CreateSearchAlertModal from '../SearchAlert/Index';
import { DisclaimerText } from '../DisclaimerText/DisclaimerText';

import styles from './SearchResults.module.css';

const GlobalResults = lazy(() => import('../GlobalResults/GlobalResults'));

export const SearchResults = ({ autogenConfig }: { autogenConfig?: AutogenConfig }) => {
  const [searchAlertInfo, setSearchAlertInfo] = useState<{ deleteAlert: string; authKey: string } | null>(null);
  const [, setPendingItemToSave, removePendingItemToSave] = useLocalStorage<PendingUserAccountItem | null>(PENDING_USER_ACCOUNT_ITEM_KEY);
  const { isDisclaimerTextBelowFold } = useDisclaimerTextVisibilityStore();
  const { breakpoint = 0 } = useBreakpoint();
  const { data: results } = useListings();
  const { isError, mutate } = useAiSearch();
  const { featureFlags, t } = useAppContext();
  const isPrivateOffice = useSearchStore((state) => state.privateOffice);
  const { openUserAccountModal } = useUserAccount();
  const { isLoaded, isSignedIn } = useAuth();
  const setSearchMode = useSearchStore((state) => state.setSearchMode);

  const searchLocation = useLocation();

  const setPromptValue = usePromptStore((state) => state.setPromptValue);

  const { isMapActive, updateIsMapView, hasMounted } = useMapVisibilityStore(
    useShallow((state) => ({
      isMapActive: state.isMapActive,
      updateIsMapView: state.actions.updateIsMapView,
      hasMounted: state.hasMounted,
    })),
  );
  const searchAlertModalState = useModalState();

  usePendingUserAccountItemAfterAuth(searchAlertModalState);

  const handleSearchAlertModalOpenChange = useCallback(
    (isOpen: boolean) => {
      if (!isOpen) {
        removePendingItemToSave();
      }
    },
    [removePendingItemToSave],
  );
  const unsubscribeState = useModalState();
  const { setOpen: setUnsubscribeOpen } = unsubscribeState;

  const handleUnsubscribeOpenChange = useCallback(
    (isOpen: boolean) => {
      setUnsubscribeOpen(isOpen);

      if (!isOpen) {
        setSearchAlertInfo(null);
        window.history.replaceState({}, '', window.location.pathname);
      }
    },
    [setUnsubscribeOpen],
  );

  useRegisterPageSkill(
    definePageSkill({
      type: 'perform-ai-search',
      description: 'Perform an AI search when the user describes the property they are looking for',
      execute: (skillData) => {
        setSearchMode(SearchMode.AI);
        setPromptValue(skillData.prompt);
        mutate(skillData.prompt);
      },
      responseSchema: {
        schemaDefinition: {
          prompt: 'The prompt to perform the AI search',
        },
        mandatoryFields: ['prompt'],
      },
    }),
  );

  const isMapView = useMemo(() => Boolean(featureFlags.isMapEnabled && isMapActive), [featureFlags.isMapEnabled, isMapActive]);

  useEffect(() => {
    const query = new URLSearchParams(window.location.search);
    const deleteAlert = query.get('deleteAlert');
    const authKey = query.get('authKey');
    if (deleteAlert && authKey) {
      setSearchAlertInfo({ deleteAlert, authKey });
      setUnsubscribeOpen(true);
    }
  }, [setUnsubscribeOpen]);

  const handleSearchAlertClick = useCallback(() => {
    if (isLoaded && isSignedIn) {
      searchAlertModalState.open();
    } else {
      openUserAccountModal({ signInTrigger: Trigger.SavedSearch, triggerId: SEARCH_ALERT_CTA_ELEMENT_ID });
      setPendingItemToSave({ type: PendingUserAccountItemType.SEARCH_ALERT });
    }
  }, [isLoaded, isSignedIn, openUserAccountModal, searchAlertModalState, setPendingItemToSave]);

  const searchMode = useSearchStore((state) => state.searchMode);

  const isAiSearchError = useMemo(() => isError && searchMode === SearchMode.AI, [isError, searchMode]);

  useEffect(() => {
    const source = new URLSearchParams(searchLocation.search).get('source');

    if (source === 'sa-overview') {
      setPromptValue(t('search.userAccount.searchAlert.creation.action.savedSearch'));
    }
  }, [t, setPromptValue]);

  return (
    <div className={styles.container}>
      <div>
        <div className={styles.resultsContainer} data-map-view={isMapView} data-testid="search-components_results-list">
          <div>
            <InfoRow autogenConfig={autogenConfig} isMapView={isMapView} updateIsMapView={updateIsMapView} />
            <SearchResultList onClick={handleSearchAlertClick} isMapView={isMapView} />
            {!isPrivateOffice && <WiderAreaResults isMapView={isMapView} />}
            {!isAiSearchError && <DisclaimerText isMapView={isMapView} />}
          </div>
          <ConditionalMapContainer isMapModalOpen={searchAlertModalState.isOpen} />
        </div>
      </div>
      {hasMounted && isPrivateOffice && results.listings.length === 0 && (
        <Suspense>
          <GlobalResults isMapView={isMapView} />
        </Suspense>
      )}
      {searchAlertModalState.isOpen && (
        <Suspense>
          <CreateSearchAlertModal modalState={searchAlertModalState} onOpenChange={handleSearchAlertModalOpenChange} />
        </Suspense>
      )}
      {unsubscribeState.isOpen && searchAlertInfo && (
        <Suspense>
          <UnsubscribeDialog modalState={unsubscribeState} onOpenChange={handleUnsubscribeOpenChange} searchAlertInfo={searchAlertInfo} />
        </Suspense>
      )}
      {results.listings.length > 0 && (
        <StickyFooter isFixed isVisible={isDisclaimerTextBelowFold}>
          <MapViewButton />
          {breakpoint <= Breakpoints.TABLET_PORTRAIT && (
            <SearchAlertButton
              data-testid="search-components_sticky-footer_search-alert-button"
              hasSearchAlertInfo
              context="sticky-footer"
            />
          )}
        </StickyFooter>
      )}
    </div>
  );
};
